import React, { useState, useCallback, useRef } from 'react';
import { useCanvasAnimation } from '../../../hooks/useCanvasAnimation';

export function EscapeVelocityChart() {
  const [rProbe, setRProbe] = useState<number>(3.0);
  const isDraggingRef = useRef<boolean>(false);

  const draw = useCallback((ctx: CanvasRenderingContext2D, deltaTime: number, totalTime: number) => {
    const width = ctx.canvas.clientWidth;
    const height = ctx.canvas.clientHeight;

    // Clear background to #0F172A
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, width, height);

    // Chart dimensions based on padding
    const chartLeft = 60;
    const chartRight = width - 20;
    const chartTop = 38;
    const chartBottom = height - 50;
    const chartWidth = chartRight - chartLeft;
    const chartHeight = chartBottom - chartTop;

    const getCanvasX = (r: number) => chartLeft + (r / 10) * chartWidth;
    const getCanvasY = (v: number) => chartBottom - Math.min(1.15, v) * chartHeight;

    // 1. DRAW SHADED NO ESCAPE ZONE (r < 1.0)
    const ehX = getCanvasX(1.0);
    ctx.fillStyle = 'rgba(239, 68, 68, 0.08)';
    ctx.fillRect(chartLeft, chartTop, ehX - chartLeft, chartHeight);

    // No Escape Zone Text Label
    ctx.fillStyle = 'rgba(239, 68, 68, 0.5)';
    ctx.font = 'italic bold 12px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('NO ESCAPE ZONE', chartLeft + (ehX - chartLeft) / 2, chartTop + chartHeight / 2 - 10);
    ctx.font = 'italic 9px sans-serif';
    ctx.fillText('(Exceeds c)', chartLeft + (ehX - chartLeft) / 2, chartTop + chartHeight / 2 + 4);

    // 2. DRAW CHART AXES AND GRID LINES
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(chartLeft, chartTop);
    ctx.lineTo(chartLeft, chartBottom);
    ctx.lineTo(chartRight, chartBottom);
    ctx.stroke();

    // Y-Axis Horizontal Gridlines (0.2c to 1.0c)
    for (let k = 1; k <= 5; k++) {
      const vVal = k * 0.2;
      const gy = chartBottom - vVal * chartHeight;

      ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
      ctx.beginPath();
      ctx.moveTo(chartLeft, gy);
      ctx.lineTo(chartRight, gy);
      ctx.stroke();

      // Label tick
      ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
      ctx.font = '9px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(`${(vVal * 100).toFixed(0)}% c`, chartLeft - 8, gy + 3);
    }

    // X-Axis Vertical Gridlines (2Rs to 10Rs)
    for (let k = 1; k <= 5; k++) {
      const rVal = k * 2.0;
      const gx = chartLeft + (rVal / 10) * chartWidth;

      ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
      ctx.beginPath();
      ctx.moveTo(gx, chartTop);
      ctx.lineTo(gx, chartBottom);
      ctx.stroke();

      // Label tick
      ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
      ctx.font = '9px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`${rVal} Rs`, gx, chartBottom + 16);
    }

    // 3. DRAW SPEED OF LIGHT SPEED LIMIT (y = c = 1.0)
    ctx.strokeStyle = '#FBBF24';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 3]);
    ctx.beginPath();
    ctx.moveTo(chartLeft, chartTop);
    ctx.lineTo(chartRight, chartTop);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = '#FBBF24';
    ctx.font = 'bold 9px sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('Speed of Light Limit (c)', chartRight - 6, chartTop - 6);

    // 4. DRAW EVENT HORIZON VERTICAL BORDER (r = 1.0)
    ctx.strokeStyle = '#EF4444';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(ehX, chartTop);
    ctx.lineTo(ehX, chartBottom);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = '#EF4444';
    ctx.font = 'bold 9px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Event Horizon (1 Rs)', ehX + 6, chartTop + 14);

    // 5. DRAW THE ESCAPE VELOCITY CURVE: v = c * sqrt(Rs / r)
    ctx.lineWidth = 3;
    const curveGrad = ctx.createLinearGradient(chartLeft, 0, chartRight, 0);
    curveGrad.addColorStop(0, '#EF4444'); // Red near singularity
    curveGrad.addColorStop(0.12, '#F59E0B'); // Orange/Yellow at horizon
    curveGrad.addColorStop(0.5, '#10B981'); // Green far away
    ctx.strokeStyle = curveGrad;

    ctx.beginPath();
    for (let r = 0.15; r <= 10.0; r += 0.05) {
      const cx = getCanvasX(r);
      const cv = Math.sqrt(1.0 / r);
      const cy = getCanvasY(cv);

      if (r === 0.15) {
        ctx.moveTo(cx, cy);
      } else {
        ctx.lineTo(cx, cy);
      }
    }
    ctx.stroke();

    // 6. DRAW DRAGGABLE PROBE AND TOOLTIP
    const px = getCanvasX(rProbe);
    const pv = Math.sqrt(1.0 / rProbe);
    const py = getCanvasY(pv);

    // Draw cyan probe dot
    ctx.fillStyle = '#22D3EE';
    ctx.beginPath();
    ctx.arc(px, py, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.arc(px, py, 3, 0, Math.PI * 2);
    ctx.fill();

    // Determine tooltip color based on velocity limit
    let tColor = '#10B981'; // Green
    if (pv > 0.99) {
      tColor = '#EF4444'; // Red (swallowed)
    } else if (pv > 0.5) {
      tColor = '#FBBF24'; // Yellow
    }

    // Draw tooltip box
    const tx = px + 12 > width - 150 ? px - 142 : px + 12;
    const ty = py - 40 < chartTop ? py + 12 : py - 40;

    ctx.fillStyle = 'rgba(11, 15, 25, 0.9)';
    ctx.strokeStyle = tColor;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(tx, ty, 130, 36, 4);
    ctx.fill();
    ctx.stroke();

    // Tooltip data text
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 9px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(`Distance: ${rProbe.toFixed(2)} Rs`, tx + 6, ty + 14);

    ctx.fillStyle = tColor;
    ctx.fillText(
      pv > 1.0 
        ? `Escape Vel: >100% of c ☠` 
        : `Escape Vel: ${(pv * 100).toFixed(0)}% of c`, 
      tx + 6, 
      ty + 26
    );

    // 7. CHART LABEL OVERLAYS
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 11px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Distance from Singularity (Schwarzschild Radii - Rs)', width / 2, chartBottom + 36);

    // Y-Axis Vertical text
    ctx.save();
    ctx.translate(16, height / 2 - 10);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Escape Velocity Required (% of Light Speed)', 0, 0);
    ctx.restore();

    // Chart header info
    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Interactive Probe: Drag the dot to inspect escape velocities', 16, 22);
  }, [rProbe]);

  // Handle drag and movement tracking
  const handleStartDrag = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    
    const chartLeft = 60;
    const chartRight = width - 20;
    const chartWidth = chartRight - chartLeft;
    
    const chartTop = 38;
    const chartBottom = height - 50;
    const chartHeight = chartBottom - chartTop;

    const px = chartLeft + (rProbe / 10) * chartWidth;
    const pv = Math.sqrt(1.0 / rProbe);
    const py = chartBottom - Math.min(1.15, pv) * chartHeight;

    const distance = Math.sqrt(Math.pow(mx - px, 2) + Math.pow(my - py, 2));

    if (distance < 20) {
      isDraggingRef.current = true;
    }
  };

  const handleDrag = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDraggingRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;

    const width = canvas.clientWidth;
    const chartLeft = 60;
    const chartRight = width - 20;
    const chartWidth = chartRight - chartLeft;

    const pct = (mx - chartLeft) / chartWidth;
    const r = Math.max(0.12, Math.min(9.8, pct * 10.0));
    setRProbe(r);
  };

  const handleStopDrag = () => {
    isDraggingRef.current = false;
  };

  const canvasRef = useCanvasAnimation(draw, { height: 340 });

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <canvas
        ref={canvasRef}
        onMouseDown={handleStartDrag}
        onMouseMove={handleDrag}
        onMouseUp={handleStopDrag}
        onMouseLeave={handleStopDrag}
        className="block w-full h-full cursor-pointer"
      />
    </div>
  );
}
